import React, { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { db } from "@/config/firebase";
import { collection, getDocs, doc, updateDoc, deleteDoc, addDoc, serverTimestamp, setDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Users, Calendar, Database, Trash2, Plus, RefreshCw, AlertTriangle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || (import.meta && import.meta.env && import.meta.env.REACT_APP_BACKEND_URL);

function slugLoginName(from) {
  const raw = String(from || "").trim();
  if (!raw) return "user" + Math.floor(Math.random()*1000);
  const parts = raw.split(/\s+/);
  let base = raw;
  if (parts.length >= 2) {
    const first = parts[0];
    const last = parts[parts.length-1];
    base = `${first}${last[0] || ""}`;
  }
  base = base.replace(/[äÄ]/g, "ae").replace(/[öÖ]/g, "oe").replace(/[üÜ]/g, "ue").replace(/[ß]/g, "ss").replace(/[^a-zA-Z0-9]/g, "").toLowerCase();
  return base || "user" + Math.floor(Math.random()*1000);
}

export default function Admin() {
  const { role } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);

  // Training Generator
  const [train, setTrain] = useState({
    from: new Date().toISOString().slice(0,10),
    to: (() => { const d = new Date(); d.setMonth(d.getMonth()+6); return d.toISOString().slice(0,10); })(),
    mon_active: true, mon_hh: 20, mon_mm: 0, mon_gym: 'DKG', mon_loc: 'DKG Köln',
    thu_active: true, thu_hh: 20, thu_mm: 0, thu_gym: 'Weyerthal', thu_loc: 'Weyerthal Köln'
  });

  if (role !== 'admin') return (
    <div className="min-h-screen bg-[#0a0608] text-rose-50 flex items-center justify-center">
      <p>Kein Zugriff</p>
    </div>
  );

  const showStatus = (msg, isError = false) => {
    setStatus(msg);
    setTimeout(() => setStatus(""), 5000);
  };

  // User Management
  const normalizeUsers = async () => {
    setLoading(true);
    const us = await getDocs(collection(db, "users"));
    let updated = 0;
    for (const d of us.docs) {
      const u = d.data();
      const display_name = u.display_name || u.name || (u.email ? String(u.email).split('@')[0] : "Spieler");
      try { await updateDoc(doc(db, "users", d.id), { display_name, name: display_name }); updated++; } catch(_){}
    }
    showStatus(`✅ ${updated} Nutzer aktualisiert`);
    setLoading(false);
  };

  const cleanupLoginNames = async () => {
    setLoading(true);
    const usersSnap = await getDocs(collection(db, "users"));
    const users = usersSnap.docs.map(d => ({ id: d.id, ...d.data() }));
    const byId = new Map(users.map(u => [u.id, u]));

    const loginsSnap1 = await getDocs(collection(db, "user_logins"));
    let deletedOrphans = 0;
    for (const d of loginsSnap1.docs) {
      const uid = (d.data().uid) || "";
      if (uid && !byId.has(uid)) { try { await deleteDoc(doc(db, "user_logins", d.id)); deletedOrphans++; } catch(_){} }
    }

    const taken = new Set();
    const produced = new Map();
    for (const u of users) {
      const base = slugLoginName(u.display_name || u.name || (u.email ? String(u.email).split('@')[0] : "user"));
      let candidate = base;
      let i = 2;
      while (taken.has(candidate)) candidate = `${base}${i++}`;
      produced.set(u.id, candidate);
      taken.add(candidate);
    }

    let changed = 0;
    const producedIds = new Set();
    for (const u of users) {
      const ln = produced.get(u.id);
      const lower = ln.toLowerCase();
      producedIds.add(lower);
      try { await updateDoc(doc(db, "users", u.id), { login_name: ln }); } catch(_){}
      try {
        const payload = { uid: u.id, updated_at: serverTimestamp() };
        if (u.email) payload.email = u.email;
        await setDoc(doc(db, "user_logins", lower), payload, { merge: true });
        changed++;
      } catch(_){}
    }

    showStatus(`✅ Orphans: ${deletedOrphans}, aktualisiert: ${changed}`);
    setLoading(false);
  };

  // Event Management
  const generateTrainings = async () => {
    setLoading(true);
    try {
      if (!train.from || !train.to) throw new Error('Datumfelder ausfüllen');
      const from = new Date(train.from + 'T00:00:00');
      const to = new Date(train.to + 'T23:59:59');
      if (from > to) throw new Error('Start-Datum muss vor End-Datum liegen');
      if (!train.mon_active && !train.thu_active) throw new Error('Mindestens ein Tag aktivieren');

      const all = await getDocs(collection(db, 'events'));
      const evs = all.docs.map(d => ({ id: d.id, ...d.data() }));

      const isSameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
      const toISOLocal = (date, hh, mm) => { const d = new Date(date); d.setHours(hh, mm, 0, 0); return d.toISOString(); };

      let created = 0, skipped = 0;
      for (let d = new Date(from); d <= to; d.setDate(d.getDate()+1)) {
        const day = d.getDay();
        let cfg = null;
        if (day === 1 && train.mon_active) cfg = { hh: Number(train.mon_hh), mm: Number(train.mon_mm), gym: train.mon_gym, loc: train.mon_loc };
        if (day === 4 && train.thu_active) cfg = { hh: Number(train.thu_hh), mm: Number(train.thu_mm), gym: train.thu_gym, loc: train.thu_loc };
        if (!cfg) continue;

        const sameDayGame = evs.some(ev => ev.type === 'game' && ev.start_datetime && isSameDay(ev.start_datetime.toDate?.() || new Date(ev.start_datetime), d));
        if (sameDayGame) { skipped++; continue; }

        await addDoc(collection(db, 'events'), {
          title: `Training ${cfg.gym}`, type: 'training',
          start_datetime: toISOLocal(d, cfg.hh, cfg.mm),
          location: cfg.loc, gym_name: cfg.gym,
          created_at: serverTimestamp(), status: 'scheduled'
        });
        created++;
      }
      showStatus(`✅ ${created} Trainings erstellt, ${skipped} übersprungen`);
    } catch (e) {
      showStatus(`❌ ${e.message}`, true);
    }
    setLoading(false);
  };

  const deleteAllTrainings = async () => {
    if (!confirm("⚠️ ALLE Trainings löschen?")) return;
    setLoading(true);
    try {
      const eventsSnap = await getDocs(collection(db, 'events'));
      let deleted = 0;
      for (const d of eventsSnap.docs) {
        if (d.data().type === 'training') { await deleteDoc(doc(db, 'events', d.id)); deleted++; }
      }
      showStatus(`✅ ${deleted} Trainings gelöscht`);
    } catch (e) {
      showStatus(`❌ ${e.message}`, true);
    }
    setLoading(false);
  };

  const deleteDuplicates = async () => {
    if (!confirm("Duplikate Events löschen?")) return;
    setLoading(true);
    try {
      const eventsSnap = await getDocs(collection(db, 'events'));
      const allEvents = eventsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      const seen = new Map();
      const duplicates = [];
      for (const event of allEvents) {
        const key = `${event.title}_${event.start_datetime?.seconds || 0}_${event.opponent || ''}`;
        if (seen.has(key)) duplicates.push(event.id);
        else seen.set(key, event.id);
      }
      for (const id of duplicates) await deleteDoc(doc(db, 'events', id));
      showStatus(`✅ ${duplicates.length} Duplikate gelöscht`);
    } catch (e) {
      showStatus(`❌ ${e.message}`, true);
    }
    setLoading(false);
  };

  // Danger Zone
  const resetAll = async () => {
    if (!confirm("🔥 WARNUNG: Alle Events, Stats und Daten löschen?")) return;
    if (!confirm("Wirklich ALLES löschen? Nicht rückgängig machbar!")) return;
    setLoading(true);
    try {
      const collections = ['events', 'game_stats', 'event_signups', 'kiosk_sessions', 'kiosk_presence', 'bets'];
      for (const col of collections) {
        const snap = await getDocs(collection(db, col));
        for (const d of snap.docs) await deleteDoc(doc(db, col, d.id));
      }
      showStatus("✅ Alles gelöscht");
    } catch (e) {
      showStatus(`❌ ${e.message}`, true);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#0a0608] text-rose-50 pb-24">
      {/* Header */}
      <header className="px-4 py-3 bg-[#12090c] border-b border-white/5 flex items-center gap-3 sticky top-0 z-10">
        <button onClick={() => navigate('/')} className="p-1">
          <ArrowLeft size={20} />
        </button>
        <h1 className="font-semibold flex-1">Admin</h1>
      </header>

      {/* Status Toast */}
      {status && (
        <div className={`mx-4 mt-4 p-3 rounded-lg text-sm ${status.includes('❌') ? 'bg-red-900/50 border border-red-500/50' : 'bg-green-900/50 border border-green-500/50'}`}>
          {status}
        </div>
      )}

      {/* Quick Links */}
      <div className="px-4 py-4 grid grid-cols-3 gap-2">
        <button onClick={() => navigate('/admin/users')} className="bg-blue-600/20 border border-blue-500/30 rounded-xl p-3 flex flex-col items-center gap-1">
          <Users size={20} className="text-blue-400" />
          <span className="text-xs">User</span>
        </button>
        <button onClick={() => navigate('/events-import')} className="bg-purple-600/20 border border-purple-500/30 rounded-xl p-3 flex flex-col items-center gap-1">
          <Calendar size={20} className="text-purple-400" />
          <span className="text-xs">Events</span>
        </button>
        <button onClick={() => navigate('/stats-import')} className="bg-green-600/20 border border-green-500/30 rounded-xl p-3 flex flex-col items-center gap-1">
          <Database size={20} className="text-green-400" />
          <span className="text-xs">Stats</span>
        </button>
      </div>

      {/* Tabs */}
      <div className="px-4">
        <Tabs defaultValue="users" className="w-full">
          <TabsList className="w-full bg-[#12090c] border border-white/5 mb-4">
            <TabsTrigger value="users" className="flex-1 text-xs">User</TabsTrigger>
            <TabsTrigger value="events" className="flex-1 text-xs">Events</TabsTrigger>
            <TabsTrigger value="system" className="flex-1 text-xs">System</TabsTrigger>
            <TabsTrigger value="danger" className="flex-1 text-xs text-red-400">Danger</TabsTrigger>
          </TabsList>

          {/* User Tab */}
          <TabsContent value="users" className="space-y-3">
            <Card className="bg-[#12090c] border-white/5">
              <CardContent className="pt-4 space-y-3">
                <h3 className="text-sm font-semibold flex items-center gap-2">
                  <Users size={16} /> Benutzerverwaltung
                </h3>
                <Button onClick={normalizeUsers} disabled={loading} className="w-full bg-white/5 hover:bg-white/10 justify-start gap-2">
                  <RefreshCw size={14} /> Display Names vereinheitlichen
                </Button>
                <Button onClick={cleanupLoginNames} disabled={loading} className="w-full bg-white/5 hover:bg-white/10 justify-start gap-2">
                  <RefreshCw size={14} /> Login-Namen aufräumen
                </Button>
                <Button onClick={() => navigate('/admin/users')} className="w-full bg-blue-600 hover:bg-blue-700 justify-start gap-2">
                  <Users size={14} /> Zur Benutzerverwaltung
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Events Tab */}
          <TabsContent value="events" className="space-y-3">
            <Card className="bg-[#12090c] border-white/5">
              <CardContent className="pt-4 space-y-3">
                <h3 className="text-sm font-semibold flex items-center gap-2">
                  <Calendar size={16} /> Trainingsserie
                </h3>
                
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs text-rose-200/50">Von</label>
                    <input type="date" value={train.from} onChange={e => setTrain(t => ({...t, from: e.target.value}))} className="w-full p-2 rounded-lg bg-white/5 border border-white/10 text-sm" />
                  </div>
                  <div>
                    <label className="text-xs text-rose-200/50">Bis</label>
                    <input type="date" value={train.to} onChange={e => setTrain(t => ({...t, to: e.target.value}))} className="w-full p-2 rounded-lg bg-white/5 border border-white/10 text-sm" />
                  </div>
                </div>

                {/* Monday */}
                <div className="p-3 rounded-lg bg-white/5 border border-white/10">
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={train.mon_active} onChange={e => setTrain(t => ({...t, mon_active: e.target.checked}))} />
                    Montag
                  </label>
                  {train.mon_active && (
                    <div className="mt-2 grid grid-cols-3 gap-2">
                      <input type="number" value={train.mon_hh} onChange={e => setTrain(t => ({...t, mon_hh: e.target.value}))} placeholder="HH" className="p-2 rounded bg-white/5 text-sm text-center" />
                      <input value={train.mon_gym} onChange={e => setTrain(t => ({...t, mon_gym: e.target.value}))} placeholder="Halle" className="p-2 rounded bg-white/5 text-sm" />
                      <input value={train.mon_loc} onChange={e => setTrain(t => ({...t, mon_loc: e.target.value}))} placeholder="Ort" className="p-2 rounded bg-white/5 text-sm" />
                    </div>
                  )}
                </div>

                {/* Thursday */}
                <div className="p-3 rounded-lg bg-white/5 border border-white/10">
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={train.thu_active} onChange={e => setTrain(t => ({...t, thu_active: e.target.checked}))} />
                    Donnerstag
                  </label>
                  {train.thu_active && (
                    <div className="mt-2 grid grid-cols-3 gap-2">
                      <input type="number" value={train.thu_hh} onChange={e => setTrain(t => ({...t, thu_hh: e.target.value}))} placeholder="HH" className="p-2 rounded bg-white/5 text-sm text-center" />
                      <input value={train.thu_gym} onChange={e => setTrain(t => ({...t, thu_gym: e.target.value}))} placeholder="Halle" className="p-2 rounded bg-white/5 text-sm" />
                      <input value={train.thu_loc} onChange={e => setTrain(t => ({...t, thu_loc: e.target.value}))} placeholder="Ort" className="p-2 rounded bg-white/5 text-sm" />
                    </div>
                  )}
                </div>

                <Button onClick={generateTrainings} disabled={loading} className="w-full bg-green-600 hover:bg-green-700">
                  <Plus size={14} className="mr-2" /> Trainings erstellen
                </Button>
              </CardContent>
            </Card>

            <Card className="bg-[#12090c] border-white/5">
              <CardContent className="pt-4 space-y-3">
                <h3 className="text-sm font-semibold">Bereinigung</h3>
                <Button onClick={deleteDuplicates} disabled={loading} className="w-full bg-orange-600/20 hover:bg-orange-600/30 text-orange-300 justify-start gap-2">
                  <Trash2 size={14} /> Duplikate löschen
                </Button>
                <Button onClick={deleteAllTrainings} disabled={loading} className="w-full bg-red-600/20 hover:bg-red-600/30 text-red-300 justify-start gap-2">
                  <Trash2 size={14} /> Alle Trainings löschen
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* System Tab */}
          <TabsContent value="system" className="space-y-3">
            <Card className="bg-[#12090c] border-white/5">
              <CardContent className="pt-4 space-y-3">
                <h3 className="text-sm font-semibold">Quick Links</h3>
                <div className="space-y-2">
                  <Button onClick={() => navigate('/import')} className="w-full bg-white/5 hover:bg-white/10 justify-start">
                    Kader importieren
                  </Button>
                  <Button onClick={() => navigate('/calendar')} className="w-full bg-white/5 hover:bg-white/10 justify-start">
                    Kalender
                  </Button>
                  <Button onClick={() => navigate('/tournaments')} className="w-full bg-white/5 hover:bg-white/10 justify-start">
                    Turniere
                  </Button>
                  <Button onClick={() => navigate('/chat')} className="w-full bg-white/5 hover:bg-white/10 justify-start">
                    Team Chat
                  </Button>
                  <Button onClick={() => navigate('/gallery')} className="w-full bg-white/5 hover:bg-white/10 justify-start">
                    Galerie
                  </Button>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-[#12090c] border-white/5">
              <CardContent className="pt-4">
                <h3 className="text-sm font-semibold mb-2">App Info</h3>
                <div className="space-y-1 text-xs text-rose-200/60">
                  <p>Kiosk: GPS-frei, Session startet bei 3 Spielern</p>
                  <p>Chat: AI-Bildgenerierung (DALL-E 3)</p>
                  <p>Inaktivitäts-Timeout: 30 Minuten</p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Danger Tab */}
          <TabsContent value="danger" className="space-y-3">
            <Card className="bg-red-950/20 border-red-500/30">
              <CardContent className="pt-4 space-y-3">
                <h3 className="text-sm font-semibold text-red-400 flex items-center gap-2">
                  <AlertTriangle size={16} /> Gefahrenzone
                </h3>
                <p className="text-xs text-rose-200/50">
                  Diese Aktionen können nicht rückgängig gemacht werden!
                </p>
                <Button onClick={resetAll} disabled={loading} className="w-full bg-red-600 hover:bg-red-700">
                  🔥 ALLES ZURÜCKSETZEN
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
